import { Alert, Box, Chip, LinearProgress, Stack, Typography } from "@mui/material";
import type { FC } from "react";
import { useState } from "react";
import CustomerSelect from "../../components/customerSelect/CustomerSelect";
import dayjs, { ISO_DATE_FORMAT } from "../../dayjs";
import ComparisonPanes from "./components/ComparisonPanes";
import KpiSections from "./components/KpiSections";
import OrdersBarChart from "./components/OrdersBarChart";
import RegionBreakdownChart from "./components/RegionBreakdownChart";
import ReportFilterPanel from "./components/ReportFilterPanel";
import EurExchangeRateChip from "./components/EurExchangeRateChip";
import ReportHeader from "./components/ReportHeader";
import { summarySections } from "./kpiSections";
import { regionOrderShareItems } from "./regionChartItems";
import { REPORTS } from "./reportCatalog";
import { formatActiveFilters } from "./statisticsFormat";
import { useAppliedFilters } from "./useAppliedFilters";
import { useCustomerOrdersTrend } from "./useCustomerOrdersTrend";
import { useCustomerStatistics } from "./useCustomerStatistics";

interface PaneFilters {
  readonly customerId: string | null;
  readonly from: string;
  readonly to: string;
}

function currentYearFilters(): PaneFilters {
  return {
    customerId: null,
    from: dayjs().startOf("year").format(ISO_DATE_FORMAT),
    to: dayjs().format(ISO_DATE_FORMAT),
  };
}

function previousYearFilters(): PaneFilters {
  const previousYear = dayjs().subtract(1, "year");
  return {
    customerId: null,
    from: previousYear.startOf("year").format(ISO_DATE_FORMAT),
    to: previousYear.endOf("year").format(ISO_DATE_FORMAT),
  };
}

interface PaneProps {
  readonly initialFilters: PaneFilters;
  readonly includeMargin: boolean;
}

const PeriodPane: FC<PaneProps> = ({ initialFilters, includeMargin }) => {
  const { draft, applied, isDirty, setDraft, apply } = useAppliedFilters<PaneFilters>(initialFilters);
  const range = { from: applied.from, to: applied.to };
  const statistics = useCustomerStatistics(applied.customerId, { ...range, includeMargin });
  const trend = useCustomerOrdersTrend(applied.customerId, { ...range, granularity: "MONTH" });
  const customerName = statistics.data?.customerName ?? "—";

  return (
    <Box>
      <ReportFilterPanel
        subject={
          <CustomerSelect
            size="small"
            customerId={draft.customerId}
            onChange={(customerId) => setDraft({ ...draft, customerId })}
          />
        }
        dateLabel="Zakres dat"
        range={draft}
        onRangeChange={({ from, to }) => setDraft({ ...draft, from, to })}
        onApply={apply}
        applyDisabled={!isDirty}
        activeFilters={formatActiveFilters(customerName, applied.from, applied.to)}
      />
      {!applied.customerId && (
        <Typography sx={{ color: "text.secondary" }}>Wybierz klienta, aby zobaczyć statystyki</Typography>
      )}
      {(statistics.isLoading || trend.isLoading) && <LinearProgress data-testid="loading-indicator" />}
      {statistics.error && <Alert severity="error">Nie udało się pobrać statystyk.</Alert>}
      {statistics.data && (
        <>
          <EurExchangeRateChip eurExchangeRate={statistics.data.eurExchangeRate} />
          <KpiSections sections={summarySections(statistics.data.summary)} />
          <RegionBreakdownChart
            title="Ilość zamówień — podział na regiony"
            items={regionOrderShareItems(statistics.data.byRegion)}
          />
        </>
      )}
      {trend.error && <Alert severity="error">Nie udało się pobrać wykresu zamówień.</Alert>}
      {trend.data && (
        <OrdersBarChart
          title="Zamówienia aktywne — podział na miesiące"
          buckets={trend.data.buckets}
          granularity="MONTH"
        />
      )}
    </Box>
  );
};

interface MarginViewChipsProps {
  readonly includeMargin: boolean;
  readonly onChange: (includeMargin: boolean) => void;
}

const MarginViewChips: FC<MarginViewChipsProps> = ({ includeMargin, onChange }) => (
  <Stack direction="row" spacing={1} sx={{ mb: 3, flexWrap: "wrap", rowGap: 1 }}>
    <Chip
      label="Widok standardowy (z marżą)"
      clickable
      color={includeMargin ? "primary" : "default"}
      variant={includeMargin ? "filled" : "outlined"}
      aria-pressed={includeMargin}
      onClick={() => onChange(true)}
    />
    <Chip
      label="Widok z karty klienta (bez marży)"
      clickable
      color={includeMargin ? "default" : "primary"}
      variant={includeMargin ? "outlined" : "filled"}
      aria-pressed={!includeMargin}
      onClick={() => onChange(false)}
    />
  </Stack>
);

const ClientsComparisonReportPage: FC = () => {
  const [includeMargin, setIncludeMargin] = useState(true);

  return (
    <Box>
      <ReportHeader report={REPORTS["customer-comparison"]} />
      <MarginViewChips includeMargin={includeMargin} onChange={setIncludeMargin} />
      <Typography variant="body2" sx={{ color: "text.secondary", mb: 3 }}>
        Domyślnie: lewy panel = bieżący rok, prawy = poprzedni rok. Daty można zmienić ręcznie.
      </Typography>
      <ComparisonPanes
        leftLabel="Bieżący okres"
        rightLabel="Okres porównawczy"
        left={<PeriodPane initialFilters={currentYearFilters()} includeMargin={includeMargin} />}
        right={<PeriodPane initialFilters={previousYearFilters()} includeMargin={includeMargin} />}
      />
    </Box>
  );
};

export default ClientsComparisonReportPage;
